import type { GameAction } from '../game/actions'
import type { GameSettings, GameState } from '../game/types'

export const WS_PATH = '/ws'
export const ROOM_CODE_LENGTH = 4
export const MAX_SEATS = 6

/** ที่นั่งในห้อง — id ตรงกับ Player.id ใน GameState เสมอ */
export interface Seat {
  id: number
  name: string
  characterId: string
  professionId: string
  isBot: boolean
  connected: boolean
}

export interface RoomView {
  code: string
  seats: Seat[]
  settings: GameSettings
  started: boolean
  hostSeat: number
}

export type ClientMsg =
  | { t: 'create'; name: string; characterId: string; professionId: string }
  | { t: 'join'; code: string; name: string; characterId: string; professionId: string }
  | { t: 'rejoin'; code: string; token: string }
  | { t: 'seat'; name?: string; characterId?: string; professionId?: string }
  | { t: 'addBot' }
  | { t: 'removeSeat'; id: number }
  | { t: 'settings'; settings: Partial<GameSettings> }
  | { t: 'start' }
  | { t: 'action'; action: GameAction }
  | { t: 'ping' }

export type ServerMsg =
  /** ตอบกลับ create/join/rejoin — token ใช้กลับเข้าห้องเดิมเมื่อเน็ตหลุด */
  | { t: 'welcome'; code: string; seatId: number; token: string; room: RoomView; g: GameState | null }
  | { t: 'room'; room: RoomView }
  | { t: 'state'; g: GameState; room: RoomView }
  | { t: 'error'; code: ErrorCode; message: string }
  | { t: 'bye'; message: string }
  | { t: 'pong' }

export type ErrorCode =
  | 'no-room'
  | 'room-full'
  | 'already-started'
  | 'not-host'
  | 'bad-token'
  | 'not-your-turn'
  | 'bad-message'

export const ERROR_TEXT: Record<ErrorCode, string> = {
  'no-room': 'ไม่พบห้องนี้ — โค้ดอาจพิมพ์ผิด หรือห้องถูกปิดไปแล้ว',
  'room-full': 'ห้องเต็มแล้ว',
  'already-started': 'ห้องนี้เริ่มเกมไปแล้ว',
  'not-host': 'เฉพาะเจ้าของห้องเท่านั้นที่ตั้งค่าได้',
  'bad-token': 'กลับเข้าห้องไม่ได้ — เริ่มใหม่จากหน้าแรก',
  'not-your-turn': 'ยังไม่ถึงตาของคุณ',
  'bad-message': 'ข้อความผิดรูปแบบ',
}

/** สร้างโค้ดห้อง — ตัดอักษรที่อ่านสับสน (0/O, 1/I) ออก เพราะต้องบอกกันปากเปล่า */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

/** เวลาที่เซิร์ฟเวอร์รอก่อนเล่นแทนผู้เล่นที่หลุดไป (มิลลิวินาที) */
export const AFK_TAKEOVER_MS = 20_000
/**
 * ความยาวอนิเมชันลูกเต๋าที่เซิร์ฟเวอร์หน่วงให้ทุกคนเห็นตรงกัน
 * ต้องยาวกว่าเวลาที่ฟิสิกส์ใช้หยุดนิ่ง (~0.9 วิ) ไม่งั้นหมากจะเริ่มเดินทั้งที่เต๋ายังกลิ้ง
 */
export const ROLL_ANIM_MS = 1350
/** ห้องที่ไม่มีใครเชื่อมต่อเกินเวลานี้จะถูกเก็บกวาด */
export const ROOM_TTL_MS = 6 * 60 * 60 * 1000
